/**
 * Utility functions for the HTML editor
 */

/**
 * Apply formatting to the current selection
 *
 * @param {string} formatType - The type of formatting to apply
 */
export const applyFormatting = (formatType) => {
  switch (formatType) {
    case "bold":
      document.execCommand("bold", false, null);
      break;
    case "italic":
      document.execCommand("italic", false, null);
      break;
    case "heading1":
      document.execCommand("formatBlock", false, "h1");
      break;
    case "heading2":
      document.execCommand("formatBlock", false, "h2");
      break;
    case "heading3":
      document.execCommand("formatBlock", false, "h3");
      break;
    case "alignLeft":
      document.execCommand("justifyLeft", false, null);
      break;
    case "alignCenter":
      document.execCommand("justifyCenter", false, null);
      break;
    case "alignRight":
      document.execCommand("justifyRight", false, null);
      break;
    case "bullet":
      document.execCommand("insertUnorderedList", false, null);
      break;
    default:
      console.warn(`Unknown format type: ${formatType}`);
  }
};

/**
 * Alternative approach to apply formatting when execCommand fails
 *
 * @param {string} formatType - The type of formatting to apply
 * @param {Range} range - The selection range
 */
export const applyFormattingAlternative = (formatType, range) => {
  if (!range) return;

  const selection = window.getSelection();
  if (!selection.rangeCount) return;

  // If the range is collapsed (just a cursor), try to select the parent element
  if (range.collapsed) {
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
      range.selectNode(node.parentElement);
    }
  }

  // Apply formatting based on type
  switch (formatType) {
    case "bold": {
      const strong = document.createElement("strong");
      try {
        range.surroundContents(strong);
      } catch (e) {
        // If surroundContents fails (e.g., with partial selections), use a different approach
        const fragment = range.extractContents();
        strong.appendChild(fragment);
        range.insertNode(strong);
      }
      break;
    }
    case "italic": {
      const em = document.createElement("em");
      try {
        range.surroundContents(em);
      } catch (e) {
        const fragment = range.extractContents();
        em.appendChild(fragment);
        range.insertNode(em);
      }
      break;
    }
    case "heading1":
    case "heading2":
    case "heading3": {
      const tagName = formatType.replace("heading", "h");
      const heading = document.createElement(tagName);

      // Find the closest block element
      let node = range.commonAncestorContainer;
      while (node && node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode;
      }

      // If we found a block element, replace it with our heading
      if (node && node.nodeType === Node.ELEMENT_NODE) {
        const tempRange = document.createRange();
        tempRange.selectNode(node);
        const fragment = tempRange.extractContents();
        heading.appendChild(fragment);
        tempRange.insertNode(heading);
      } else {
        // Otherwise, just wrap the selection
        try {
          const fragment = range.extractContents();
          heading.appendChild(fragment);
          range.insertNode(heading);
        } catch (e) {
          console.error("Error applying heading:", e);
        }
      }
      break;
    }
    case "alignLeft":
    case "alignCenter":
    case "alignRight": {
      // Find the closest block element
      let node = range.commonAncestorContainer;
      while (node && node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode;
      }

      if (node && node.nodeType === Node.ELEMENT_NODE) {
        const textAlign = formatType.replace("align", "").toLowerCase();
        node.style.textAlign = textAlign;
      }
      break;
    }
    case "bullet": {
      // Create a list
      const ul = document.createElement("ul");
      const li = document.createElement("li");

      try {
        const fragment = range.extractContents();
        li.appendChild(fragment);
        ul.appendChild(li);
        range.insertNode(ul);
      } catch (e) {
        console.error("Error creating bullet list:", e);
      }
      break;
    }
    default:
      console.warn(
        `Unknown format type for alternative approach: ${formatType}`
      );
  }
};

/**
 * Highlight keywords in HTML content
 *
 * @param {string} html - The HTML content to process
 * @param {Array<string>} keywords - Array of keywords to highlight
 * @param {string} highlightClass - CSS class to apply to highlighted keywords
 * @returns {string} HTML with highlighted keywords
 */
export const highlightKeywordsInHtml = (
  html,
  keywords = [],
  highlightClass = "keyword-highlight"
) => {
  if (!html || !keywords || keywords.length === 0) return html;

  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Function to escape regex special characters
  const escapeRegExp = (string) =>
    string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Create a regex pattern for all keywords
  const escapedKeywords = keywords.map((kw) => escapeRegExp(kw));
  const pattern = new RegExp(`\\b(${escapedKeywords.join("|")})\\b`, "gi");

  // Function to process text nodes
  const processTextNode = (textNode) => {
    const text = textNode.nodeValue;
    if (!pattern.test(text)) return;

    // Reset the pattern's lastIndex
    pattern.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, match.index))
        );
      }

      // Create a span for the matched keyword
      const span = document.createElement("span");
      span.className = highlightClass;
      span.textContent = match[0];
      fragment.appendChild(span);

      lastIndex = pattern.lastIndex;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    // Replace the original text node with the fragment
    textNode.parentNode.replaceChild(fragment, textNode);
  };

  // Recursively process all text nodes
  const walkTextNodes = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node);
      return;
    }

    // Skip highlighting in script, style, and already highlighted elements
    if (
      node.nodeName === "SCRIPT" ||
      node.nodeName === "STYLE" ||
      (node.nodeName === "SPAN" && node.className === highlightClass)
    ) {
      return;
    }

    // Process child nodes
    const childNodes = Array.from(node.childNodes);
    childNodes.forEach(walkTextNodes);
  };

  // Process all text nodes in the HTML
  walkTextNodes(tempDiv);

  return tempDiv.innerHTML;
};

/**
 * Get the computed style of the current selection
 *
 * @returns {Object} An object containing the computed styles
 */
export const getSelectionStyle = () => {
  const selection = window.getSelection();
  if (!selection.rangeCount) return {};

  const range = selection.getRangeAt(0);
  const node = range.startContainer;

  // If it's a text node, get its parent element
  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;

  if (!element) return {};

  const computedStyle = window.getComputedStyle(element);

  return {
    isBold: computedStyle.fontWeight >= 700,
    isItalic: computedStyle.fontStyle === "italic",
    isUnderline: computedStyle.textDecoration.includes("underline"),
    textAlign: computedStyle.textAlign,
    color: computedStyle.color,
    fontSize: computedStyle.fontSize,
    fontFamily: computedStyle.fontFamily,
  };
};

/**
 * Extract content from HTML by section
 *
 * @param {string} html - The HTML content to extract from
 * @returns {Object} An object containing extracted content by section
 */
export const extractContentBySections = (html) => {
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Find all sections in the HTML
  const sections = tempDiv.querySelectorAll("[data-section]");

  // Create an object to store the extracted content
  const extractedContent = {
    // Store the full HTML as a fallback
    fullHtml: html,
    // Store the sections by their data-section attribute
    sections: {},
  };

  // Extract content from each section
  sections.forEach((section) => {
    const sectionName = section.getAttribute("data-section");
    if (sectionName) {
      extractedContent.sections[sectionName] = {
        html: section.innerHTML,
        text: section.textContent,
        // Store any other attributes that might be useful
        attributes: Array.from(section.attributes)
          .filter((attr) => attr.name !== "data-section")
          .reduce((obj, attr) => {
            obj[attr.name] = attr.value;
            return obj;
          }, {}),
      };
    }
  });

  // Extract personal info (name, contact details)
  const personalInfo = tempDiv.querySelector("[data-section='personal-info']");
  if (personalInfo) {
    extractedContent.personalInfo = {
      html: personalInfo.innerHTML,
      text: personalInfo.textContent,
    };
  }

  // Extract education items
  const educationItems = tempDiv.querySelectorAll(
    "[data-element='education-item']"
  );
  if (educationItems.length > 0) {
    extractedContent.education = Array.from(educationItems).map((item) => ({
      html: item.innerHTML,
      text: item.textContent,
      degree:
        item.querySelector("[data-element='education-degree']")?.textContent ||
        "",
      institution:
        item.querySelector("[data-element='education-institution']")
          ?.textContent || "",
    }));
  }

  // Extract work experience items
  const jobItems = tempDiv.querySelectorAll("[data-element='job']");
  if (jobItems.length > 0) {
    extractedContent.jobs = Array.from(jobItems).map((item) => ({
      html: item.innerHTML,
      text: item.textContent,
      title:
        item.querySelector("[data-element='job-title']")?.textContent || "",
      period:
        item.querySelector("[data-element='job-period']")?.textContent || "",
      description:
        item.querySelector("[data-element='job-description']")?.textContent ||
        "",
    }));
  }

  return extractedContent;
};

/**
 * Merge user content into template HTML
 *
 * @param {string} templateHtml - The template HTML to merge into
 * @param {Object} userContent - The user content extracted from the original HTML
 * @returns {string} The merged HTML
 */
export const mergeContentIntoTemplate = (templateHtml, userContent) => {
  // If we don't have proper user content, return the template HTML
  if (!userContent || !userContent.sections) {
    console.warn("No valid user content to merge, returning template HTML");
    return templateHtml;
  }

  // Create a temporary div to parse the template HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = templateHtml;

  try {
    // Find all sections in the template
    const templateSections = tempDiv.querySelectorAll("[data-section]");

    // For each section in the template, try to find matching user content
    templateSections.forEach((section) => {
      const sectionName = section.getAttribute("data-section");

      // If we have user content for this section, merge it
      if (sectionName && userContent.sections[sectionName]) {
        // Special handling for different section types
        if (sectionName === "personal-info") {
          // For personal info, we want to keep the template structure but use the user's text
          const nameElement = section.querySelector("[data-element='name']");
          if (nameElement && userContent.personalInfo) {
            const userNameElement = document.createElement("div");
            userNameElement.innerHTML = userContent.personalInfo.html;
            const userName = userNameElement.querySelector(
              "[data-element='name']"
            );
            if (userName) {
              nameElement.textContent = userName.textContent;
            }
          }
        } else if (sectionName === "education") {
          // For education, we want to replace the entire content
          if (userContent.education && userContent.education.length > 0) {
            // Clear the template content
            section.innerHTML = "";

            // Add the section header back
            const header = document.createElement("h2");
            header.setAttribute("data-section-header", "true");
            header.setAttribute("data-element", "section-header");
            header.style.fontFamily = "Times New Roman";
            header.textContent = "Education";
            section.appendChild(header);

            // Add each education item
            userContent.education.forEach((edu) => {
              const eduDiv = document.createElement("div");
              eduDiv.setAttribute("data-element", "education-item");
              eduDiv.innerHTML = edu.html;
              section.appendChild(eduDiv);
            });
          }
        } else if (sectionName === "experience") {
          // For work experience, we want to replace the entire content
          if (userContent.jobs && userContent.jobs.length > 0) {
            // Clear the template content
            section.innerHTML = "";

            // Add the section header back
            const header = document.createElement("h2");
            header.setAttribute("data-section-header", "true");
            header.setAttribute("data-element", "section-header");
            header.style.fontFamily = "Times New Roman";
            header.textContent = "Work Experience";
            section.appendChild(header);

            // Add each job item
            userContent.jobs.forEach((job) => {
              const jobDiv = document.createElement("div");
              jobDiv.setAttribute("data-element", "job");
              jobDiv.innerHTML = job.html;
              section.appendChild(jobDiv);
            });
          }
        } else {
          // For other sections, just replace the inner HTML
          section.innerHTML = userContent.sections[sectionName].html;
        }
      }
    });

    return tempDiv.innerHTML;
  } catch (error) {
    console.error("Error merging content:", error);
    // If something goes wrong, return the user's original HTML
    return userContent.fullHtml;
  }
};
